import {
  Alert,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
} from '@mui/material';
import { authContext, Profile } from '@staffedupusa/shared-components';
import { Dispatch, FC, SetStateAction, useContext, useState } from 'react';

import { useGetApplicantDetails } from '@features/applicant/hooks/useGetApplicantDetails';
import ApplicationNotes from '@features/applicationNotes/components/ApplicationNotes/ApplicationNotes';
import { Application } from '@features/applications';
import ApplicantViewApplicantInfo from '@features/applications/components/ApplicantViewApplicantInfo/ApplicantViewApplicantInfo';
import ApplicationViewActions from '@features/applications/components/ApplicationViewActions/ApplicationViewActions';
import { sortApplicationsByDate } from '@features/applications/components/ApplicationViewModal/utils';
import ApplicationViewModalHeader from '@features/applications/components/ApplicationViewModalHeader/ApplicationViewModalHeader';
import ApplicationViewTabs from '@features/applications/components/ApplicationViewTabs/ApplicationViewTabs';
import { useGetApplicantApplications } from '@features/applications/hooks/useGetApplicantApplications';
import { lightGrey } from '@styles/colors';
import { Close } from '@mui/icons-material';

type ApplicationViewModalProps = {
  handleClose: () => void;
  application: Application | null;
  searchValue: string;
  updateRating?: (id: number, newRating: number) => void;
  defaultOnboarding?: boolean;
  setApplication: Dispatch<SetStateAction<Application | null>>;
  setApplicationData?: Dispatch<SetStateAction<Application[] | null>>;
  handlePageNo?: (newPageNo: number) => void;
};

const ApplicationViewModal: FC<ApplicationViewModalProps> = ({
  handleClose,
  application,
  searchValue,
  updateRating,
  setApplication,
  setApplicationData,
  handlePageNo,
  defaultOnboarding,
}) => {
  const { authData } = useContext(authContext);
  const [errorText, setErrorText] = useState<string>('');
  const [isApplicationNotesShown, setIsApplicationNotesShown] = useState(false);

  const { applicantApplicationsData } = useGetApplicantApplications({
    setErrorText,
    id: application?.applicant?.id,
    isCompany: authData?.user?.company ? true : false,
  });

  const { applicantDetailsData } = useGetApplicantDetails({
    applicantId: application?.applicant?.id,
  });

  const handleModalClose = () => {
    setIsApplicationNotesShown(false);
    handleClose();
  };

  if (!application) {
    return null;
  }

  return (
    <Dialog
      open
      onClose={handleModalClose}
      maxWidth="lg"
      fullWidth
      disableEnforceFocus
    >
      <DialogTitle sx={{ bgcolor: lightGrey, pb: 5 }}>
        <IconButton
          sx={{ position: 'absolute', top: 5, right: 5 }}
          onClick={handleModalClose}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ bgcolor: lightGrey }}>
        <Stack spacing={3}>
          {errorText && <Alert severity="error">{errorText}</Alert>}
          <ApplicationViewModalHeader
            application={application}
            setApplication={setApplication}
            applicantApplicationsData={sortApplicationsByDate(
              (applicantApplicationsData?.object as Application[]) || [],
            )}
            applicantDetailsData={
              applicantDetailsData?.object as Profile | null
            }
          />
          <ApplicantViewApplicantInfo
            application={application}
            applicantDetailsData={applicantDetailsData?.object || null}
            searchValue={searchValue}
            toggleApplicationNotesModal={() =>
              setIsApplicationNotesShown((prev) => !prev)
            }
            setApplication={setApplication}
            setApplicationData={setApplicationData}
            updateRating={updateRating}
          />
          {isApplicationNotesShown ? (
            <ApplicationNotes
              applicationId={application.id}
              handleClose={() => setIsApplicationNotesShown(false)}
            />
          ) : (
            <>
              <ApplicationViewActions
                handlePageNo={handlePageNo}
                setApplication={setApplication}
                application={application}
                setApplicationData={setApplicationData}
              />
              <ApplicationViewTabs
                application={application}
                applicationDetails={applicantDetailsData?.object || null}
                defaultOnboarding={defaultOnboarding}
              />
            </>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default ApplicationViewModal;
